import { useMemo, useState } from "react";
import { Button, Radio, TextInput } from "@a2z/ui";
import { useTranslations } from "@/lib/i18n/intl";
import { keys } from "@/lib/i18n/portal";
import { GOVERNORATES } from "../governorates";
import {
  GovernorateGrid,
  Icons,
  Label,
  ModalShell,
  tokens,
} from "../primitives";

export type FreeShippingRuleInput = {
  name: string;
  criteria: "amount" | "date" | "both";
  minAmount: number | null;
  startDate: string;
  endDate: string;
  zones: string[];
};

export function FreeShippingModal({
  governorates,
  onClose,
  onSave,
}: {
  governorates?: string[];
  onClose: () => void;
  onSave: (r: FreeShippingRuleInput) => void;
}) {
  const t = useTranslations();
  const ss = keys.seller.storeSettings;
  const allGovernorates =
    governorates && governorates.length > 0 ? governorates : GOVERNORATES;
  const [name, setName] = useState("");
  const [criteria, setCriteria] = useState<"amount" | "date" | "both">(
    "amount",
  );
  const [minAmount, setMinAmount] = useState("500");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [zones, setZones] = useState(new Set<string>());
  const [search, setSearch] = useState("");

  const filtered = useMemo(
    () =>
      allGovernorates.filter((g) =>
        g.toLowerCase().includes(search.toLowerCase()),
      ),
    [allGovernorates, search],
  );
  const toggleZone = (z: string) => {
    const n = new Set(zones);
    if (n.has(z)) n.delete(z);
    else n.add(z);
    setZones(n);
  };
  const allSelected = filtered.every((g) => zones.has(g));
  const toggleAll = () => {
    const n = new Set(zones);
    if (allSelected) filtered.forEach((g) => n.delete(g));
    else filtered.forEach((g) => n.add(g));
    setZones(n);
  };

  const CRITERIA = [
    {
      id: "date" as const,
      label: t(ss.criteriaDateRange),
      desc: t(ss.criteriaDateRangeDesc),
    },
    {
      id: "amount" as const,
      label: t(ss.criteriaOrderAmount),
      desc: t(ss.criteriaOrderAmountDesc),
    },
    {
      id: "both" as const,
      label: t(ss.criteriaBoth),
      desc: t(ss.criteriaBothDesc),
    },
  ];

  const valid =
    Boolean(name.trim()) &&
    zones.size > 0 &&
    (criteria === "amount" ? Boolean(minAmount) : true) &&
    (criteria !== "amount" ? Boolean(startDate) && Boolean(endDate) : true);
  const save = () =>
    onSave({
      name: name.trim(),
      criteria,
      minAmount: parseFloat(minAmount) || null,
      startDate,
      endDate,
      zones: Array.from(zones),
    });

  return (
    <ModalShell
      icon={<span style={{ fontSize: 20 }}>🚚</span>}
      title={t(ss.addFreeShipping)}
      subtitle={t(ss.freeShippingDesc)}
      onClose={onClose}
      footerHint={t(ss.governoratesSelected, {
        count: zones.size,
        total: allGovernorates.length,
      })}
      onSave={save}
      valid={valid}
      saveLabel={t(ss.addFreeShipping)}
      saveIcon
    >
      <div>
        <Label>
          {t(ss.ruleName)} <span style={{ color: tokens.danger }}>*</span>
        </Label>
        <div className="mt-1">
          <TextInput
            name="name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder={t(ss.freeRuleNamePlaceholder)}
          />
        </div>
      </div>

      <div style={{ marginTop: 16 }}>
        <Label>
          {t(ss.criteriaType)} <span style={{ color: tokens.danger }}>*</span>
        </Label>
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: 6,
            marginTop: 6,
          }}
        >
          {CRITERIA.map((c) => {
            const sel = criteria === c.id;
            return (
              <label
                key={c.id}
                style={{
                  display: "flex",
                  alignItems: "flex-start",
                  gap: 10,
                  padding: "10px 12px",
                  borderRadius: 9,
                  cursor: "pointer",
                  border: sel
                    ? `1.5px solid ${tokens.accent}`
                    : `1px solid ${tokens.border}`,
                  background: sel
                    ? `color-mix(in oklab, ${tokens.accent} 5%, white)`
                    : tokens.surface,
                }}
              >
                <Radio
                  name="criteria"
                  checked={sel}
                  onChange={() => setCriteria(c.id)}
                  size="md"
                  className="mt-0.5"
                />
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 13, fontWeight: 600 }}>{c.label}</div>
                  <div
                    style={{
                      fontSize: 12,
                      color: tokens.text3,
                      marginTop: 1,
                    }}
                  >
                    {c.desc}
                  </div>
                </div>
              </label>
            );
          })}
        </div>
      </div>

      {criteria === "amount" || criteria === "both" ? (
        <div style={{ marginTop: 14 }}>
          <Label>
            {t(ss.minOrderAmount)}{" "}
            <span style={{ color: tokens.danger }}>*</span>
          </Label>
          <div className="mt-1">
            <TextInput
              name="minAmount"
              type="number"
              value={minAmount}
              onChange={(e) => setMinAmount(e.target.value)}
              className="font-mono"
              suffixSpace={48}
              suffix={
                <span
                  className="font-mono"
                  style={{ fontSize: 12, fontWeight: 600, color: tokens.text3 }}
                >
                  {t(ss.currencyEgp)}
                </span>
              }
            />
          </div>
        </div>
      ) : null}

      {criteria === "date" || criteria === "both" ? (
        <div className="mt-3.5 grid grid-cols-1 gap-3 min-[420px]:grid-cols-2">
          <div>
            <Label>
              {t(ss.startDate)} <span style={{ color: tokens.danger }}>*</span>
            </Label>
            <div className="mt-1">
              <TextInput
                name="startDate"
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
              />
            </div>
          </div>
          <div>
            <Label>
              {t(ss.endDate)} <span style={{ color: tokens.danger }}>*</span>
            </Label>
            <div className="mt-1">
              <TextInput
                name="endDate"
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
              />
            </div>
          </div>
        </div>
      ) : null}

      <div style={{ marginTop: 18 }}>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            marginBottom: 8,
          }}
        >
          <Label>
            {t(ss.eligibleGovernorates)}{" "}
            <span style={{ color: tokens.danger }}>*</span>
          </Label>
          <Button variant="outline" size="sm" onClick={toggleAll}>
            {allSelected ? t(ss.deselectAll) : t(ss.selectAll)}
          </Button>
        </div>
        <div className="mb-2.5">
          <TextInput
            name="search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={t(ss.searchGovernorates)}
            prefixSpace={34}
            prefix={Icons.search}
          />
        </div>
        <GovernorateGrid
          governorates={filtered}
          selected={zones}
          onToggle={toggleZone}
          height={240}
        />
      </div>
    </ModalShell>
  );
}
